import React from 'react';
import Div from '../Element/Div/Div';
import Flex from '../Flex/Flex';
import BodyText from '../Typography/BodyText/BodyText';
import Gutter from '../Gutter/Gutter';
import styles from './ProgressBar.module.scss';
import { ArrayElementType } from '../../types/utils/ArrayElementType';
import { CreateProps } from '../../types/utils/CreateProps';

export const supportedProgressBarColors = [
  'purple500',
  'green700',
  'orange500',
] as const;

export type SupportedProgressBarColors = ArrayElementType<
  typeof supportedProgressBarColors
>;

const ROUND_PRECISION = 1000;

const getPercentage = (now: number, min: number, max: number) => {
  let percentage = ((now - min) / (max - min)) * 100;
  if (percentage > 100) percentage = 100;
  return Math.round(percentage * ROUND_PRECISION) / ROUND_PRECISION;
};

type ProgressBarProps = CreateProps<
  {
    /** Color of the progress bar */
    color?: SupportedProgressBarColors;
    /** Current value of the progress bar, default is 0 */
    current?: number;
    /** Minimum value of the bar, default is 0 */
    min?: number;
    /** Maximum value of the bar, default is 0 */
    max: number;
    /** Units of what progress bar measures */
    units?: string;
    /** Label of the progress bar */
    label?: string;
    /** Units of what progress bar measures */
    description?: string;
    /** Description text below the bar */
  },
  typeof Div,
  'flexItem'
>;

const ProgressBar: React.FunctionComponent<ProgressBarProps> = ({
  current = 0,
  min = 0,
  max,
  units,
  label,
  description,
  color = 'purple500',
  ...props
}) => {
  const percentage = getPercentage(current, min, max);
  return (
    <>
      <Div
        flexItem={{
          sizeMultiplier: 1,
        }}
        {...props}
      >
        <Flex spaceBetween>
          <BodyText
            color="black"
            tagName="span"
            fontWeight="bold"
            data-testid="label"
          >
            {label}
          </BodyText>
          <BodyText color="black">
            {current} /{' '}
            <BodyText color="black" tagName="span" fontWeight="bold">
              {max} {units}
            </BodyText>
          </BodyText>
        </Flex>
      </Div>

      <Gutter size="xs" />

      <div className={styles.progressBar_background}>
        <Div
          style={{
            width: `${percentage}%`,
          }}
          className={styles.progressBar}
          backgroundColor={color}
        />
      </div>

      <Gutter size="xs" />

      <BodyText color="black" tagName="span" size="sm">
        {description}
      </BodyText>
    </>
  );
};

export default ProgressBar;
